/* ============================================================================
   AGC PostHog event wiring — Airgun Capital storefront
   Loaded (defer) after the PostHog snippet in layout/theme.liquid.
   Network interception happens in an inline shim in theme.liquid (it must run
   at parse time, before deferred theme.js snapshots window.fetch); the shim
   queues matches into window.__agcQ and this file drains the queue.
   Captures commerce events on top of PostHog autocapture/pageviews:
     product_viewed, collection_viewed, store_search, cart_viewed,
     add_to_cart, checkout_clicked, newsletter_signup, giveaway_entered,
     contact_form_submitted, waitlist_joined, fitment_form_submitted
   Checkout funnel + purchases (checkout_started, payment_info_submitted,
   order_completed) come from the Shopify customer-events pixel — see
   .claude/posthog/checkout-pixel.js in the theme repo.
   Plain script on purpose — the store speed app rewrites import() in theme
   JS and breaks inline modules (see repo notes).
   ========================================================================== */
(function () {
  if (!window.posthog) return;

  /* Resolve window.posthog at call time, never cache it. The snippet installs a
     stub object and posthog-js REPLACES the global once array.js loads. array.js
     is async and this file is deferred, so it's a race: when this script wins,
     a cached reference is pinned to the dead stub and every capture after page
     load is silently dropped (verified live 2026-08-16 — add_to_cart fired once
     against 35 button clicks). Load-time captures survived only because the
     stub's queue is replayed at init. */
  function capture(name, props) {
    var p = window.posthog;
    if (p && p.capture) p.capture(name, props);
  }

  function safe(fn, fallback) {
    try {
      var v = fn();
      return v === undefined || v === null ? fallback : v;
    } catch (e) {
      return fallback;
    }
  }
  function money(cents) {
    return typeof cents === 'number' ? Math.round(cents) / 100 : undefined;
  }

  var meta = safe(function () { return window.ShopifyAnalytics.meta; }, {});
  var page = meta.page || {};
  var currency =
    safe(function () { return window.Shopify.currency.active; }) ||
    safe(function () { return window.theme.currencyCode; }) ||
    'USD';

  /* -- identify logged-in customers (guests stay anonymous) ---------------- */
  var agc = window.AGC_PH || {};
  if (agc.customerId) {
    var phId = window.posthog;
    if (phId && phId.identify) {
      phId.identify('shopify-' + agc.customerId, agc.customerEmail ? { email: agc.customerEmail } : {});
    }
  }

  /* -- cart state cache (theme broadcasts cart JSON on every change) ------- */
  var lastCart = null;
  document.addEventListener('theme:cart:change', function (e) {
    if (e && e.detail && e.detail.cart) lastCart = e.detail.cart;
  });
  function cartProps() {
    if (!lastCart) return {};
    return { cart_value: money(lastCart.total_price), cart_item_count: lastCart.item_count };
  }

  /* -- page-type events ----------------------------------------------------- */
  var productName = '';
  if (page.pageType === 'product' && meta.product) {
    var v0 = (meta.product.variants && meta.product.variants[0]) || {};
    productName = String(v0.name || document.title).split(' - ')[0];
    capture('product_viewed', {
      product_id: meta.product.id,
      product_name: productName,
      product_type: meta.product.type,
      vendor: meta.product.vendor,
      price: money(v0.price),
      currency: currency,
      handle: safe(function () { return location.pathname.split('/products/')[1].split(/[/?#]/)[0]; })
    });
  } else if (page.pageType === 'collection') {
    capture('collection_viewed', {
      handle: safe(function () { return location.pathname.split('/collections/')[1].split(/[/?#]/)[0]; }),
      resource_id: page.resourceId
    });
  } else if (page.pageType === 'searchresults') {
    var searchQuery = safe(function () { return new URLSearchParams(location.search).get('q'); }, '');
    capture('store_search', { query: searchQuery });
    /* Mirror the search into Supabase (public.store_searches, same endpoint/key
       as the gun database; insert-only RLS). The inline fetch shim only maps
       product_waitlist and gun_database URLs into queue events, so this POST
       passes through it untouched. PostHog ids come from the cookie because
       async array.js usually hasn't loaded yet when this deferred script runs. */
    if (searchQuery) {
      var phCookie = safe(function () {
        var m = document.cookie.match(/(?:^|;\s*)ph_phc_oXBsTuFqT2uZLgLeavsDpabe5rou8963eiRJ6CF7yMYr_posthog=([^;]+)/);
        var parsed = JSON.parse(decodeURIComponent(m[1]));
        /* $sesid is [lastActivityTimestamp, sessionId, sessionStartTimestamp] */
        return {
          d: parsed.distinct_id || null,
          s: (Array.isArray(parsed.$sesid) && parsed.$sesid[1]) || null
        };
      }, {});
      try {
        fetch('https://jrhcucfxfogpgrfvthlf.supabase.co/rest/v1/store_searches', {
          method: 'POST',
          headers: {
            'apikey': 'sb_publishable_fcp4v4hY4BxCc_Ri_YWdGw_4W5KlbrT',
            'Authorization': 'Bearer sb_publishable_fcp4v4hY4BxCc_Ri_YWdGw_4W5KlbrT',
            'Content-Type': 'application/json',
            'Prefer': 'return=minimal'
          },
          body: JSON.stringify({
            query: String(searchQuery).slice(0, 200),
            ph_distinct_id: phCookie.d || null,
            ph_session_id: phCookie.s || null,
            referrer: document.referrer || null,
            user_agent: navigator.userAgent
          })
        }).catch(function () {});
      } catch (e) {}
    }
  } else if (page.pageType === 'cart') {
    var cartCaptured = false;
    var captureCartView = function () {
      if (cartCaptured) return;
      cartCaptured = true;
      capture('cart_viewed', cartProps());
    };
    document.addEventListener('theme:cart:change', function once() {
      document.removeEventListener('theme:cart:change', once);
      captureCartView();
    });
    setTimeout(captureCartView, 3000);
  }

  /* -- add to cart / waitlist / fitment: drain the inline shim's queue ------ */
  var lastAddAt = 0;
  function captureAdd(props) {
    var now = Date.now();
    if (now - lastAddAt < 600) return; // shim + submit fallback can both fire
    lastAddAt = now;
    props = props || {};
    if (page.pageType === 'product' && meta.product) {
      if (!props.product_id) props.product_id = meta.product.id;
      if (!props.product_name && productName) props.product_name = productName;
    }
    props.currency = currency;
    capture('add_to_cart', props);
  }

  function parseAddPayload(body) {
    var out = {};
    try {
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        out.variant_id = body.get('id') || undefined;
        out.quantity = Number(body.get('quantity')) || 1;
      } else if (typeof body === 'string' && body.length) {
        if (body.charAt(0) === '{') {
          var j = JSON.parse(body);
          var item = (j.items && j.items[0]) || j;
          out.variant_id = item.id;
          out.quantity = Number(item.quantity) || 1;
        } else {
          var sp = new URLSearchParams(body);
          out.variant_id = sp.get('id') || undefined;
          out.quantity = Number(sp.get('quantity')) || 1;
        }
      }
    } catch (e) {}
    return out;
  }

  function rowProps(body, kind) {
    var props = {};
    try {
      var parsed = JSON.parse(body);
      var row = Array.isArray(parsed) ? parsed[0] || {} : parsed;
      if (kind === 'waitlist') {
        props.product = row.product || row.product_handle || row.sku;
        props.source = row.src || row.source;
      } else {
        props.brand = row.brand || row.make;
        props.model = row.gun || row.model;
      }
    } catch (e) {}
    return props;
  }

  function handleQueued(item) {
    if (!item) return;
    if (item.t === 'add') captureAdd(parseAddPayload(item.b));
    else if (item.t === 'waitlist') capture('waitlist_joined', rowProps(item.b, 'waitlist'));
    else if (item.t === 'fitment') capture('fitment_form_submitted', rowProps(item.b, 'fitment'));
  }

  var q = window.__agcQ;
  if (q && q.length) q.forEach(handleQueued);
  if (q) q.push = function (item) { handleQueued(item); return this.length; };
  else window.__agcQ = { push: function (item) { handleQueued(item); return 0; } };

  /* -- checkout click (canonical checkout_started comes from the pixel) ----- */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.closest('button[name="checkout"], input[name="checkout"], a[href*="/checkout"], [data-checkout-button]')) {
      capture('checkout_clicked', cartProps());
    }
  }, true);

  /* -- form submits: newsletter / giveaway / contact / cart-add fallback ----- */
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (!f || f.nodeName !== 'FORM') return;
    var action = f.getAttribute('action') || '';
    if (action.indexOf('/cart/add') !== -1) {
      try { captureAdd(parseAddPayload(new FormData(f))); } catch (err) {}
      return;
    }
    if (action.indexOf('/contact') === -1) return;
    var typeEl = f.querySelector('input[name="form_type"]');
    var formType = typeEl ? typeEl.value : '';
    if (formType === 'customer') {
      var tagsEl = f.querySelector('input[name="contact[tags]"]');
      var tags = (tagsEl && tagsEl.value) || '';
      if ((f.className || '').indexOf('giveaway-form') !== -1 || tags.indexOf('giveaway') !== -1) {
        capture('giveaway_entered', { tags: tags });
      } else {
        capture('newsletter_signup', { tags: tags, form_id: f.id || undefined });
      }
    } else if (formType === 'contact') {
      capture('contact_form_submitted', {});
    }
  }, true);
})();
